import { useEffect, useMemo, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonSpinner, IonSegment, IonSegmentButton, IonLabel,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useContentStore } from '@/store/content.store';
import { PLATFORM_IDS, type PlatformId } from '@ai-media/shared';

interface CalendarEvent {
  id: string;
  title: string;
  status: string;
  platforms: PlatformId[];
  publishAt: Date;
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const posts = useContentStore((s) => s.posts);
  const fetchPosts = useContentStore((s) => s.fetchPosts);
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    void fetchPosts({});
  }, [fetchPosts]);

  const base = useMemo(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + monthOffset);
    return date;
  }, [monthOffset]);

  const days = useMemo(() => {
    const year = base.getFullYear();
    const month = base.getMonth();
    const start = new Date(year, month, 1);
    const startWeekday = (start.getDay() + 6) % 7; // Monday-first grid
    const total = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: Date | null; events: CalendarEvent[] }> = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push({ date: null, events: [] });
    for (let day = 1; day <= total; day += 1) {
      const date = new Date(year, month, day);
      const events = posts
        .filter((post) => post.publish_at && sameDay(new Date(post.publish_at), date))
        .map((post) => ({
          id: post.id,
          title: post.title ?? (post.draft ?? '').slice(0, 24),
          status: post.status,
          platforms: post.platform_ids ?? [],
          publishAt: new Date(post.publish_at as string),
        }));
      cells.push({ date, events });
    }
    return cells;
  }, [base, posts]);

  const today = new Date();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>
            {base.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={String(monthOffset)} onIonChange={(event) => {
            const value = Number((event.target as HTMLIonSegmentElement).value);
            setMonthOffset(Number.isNaN(value) ? 0 : value);
          }}>
            <IonSegmentButton value="-1"><IonLabel>{t('common.previous')}</IonLabel></IonSegmentButton>
            <IonSegmentButton value="0"><IonLabel>{t('common.date')}</IonLabel></IonSegmentButton>
            <IonSegmentButton value="1"><IonLabel>{t('common.next')}</IonLabel></IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell">
          <div className="calendar-grid" style={{ marginBottom: 6 }}>
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((weekday) => (
              <div key={weekday} style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.72rem', color: 'var(--ion-color-medium)' }}>
                {weekday}
              </div>
            ))}
          </div>
          <div className="calendar-grid">
            {days.map((cell, index) => (
              <div key={index} className={`calendar-day${cell.date && sameDay(cell.date, today) ? ' calendar-day--today' : ''}`}>
                {cell.date && <strong>{cell.date.getDate()}</strong>}
                {cell.events.map((event) => (
                  <div
                    key={event.id}
                    className="calendar-event"
                    style={{ background: event.status === 'posted' ? '#16a34a' : event.status === 'failed' ? '#dc2626' : '#2c609d' }}
                    title={event.title}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.8rem', marginTop: 14 }}>
            {PLATFORM_IDS.join(' · ')}
          </p>
        </div>
      </IonContent>
    </IonPage>
  );
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
